import type { PostTablesLabels200DataItem } from '@ipfabric/api-client/client/schemas/postTablesLabels200DataItem';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { useRef } from 'react';
import { DeleteAssignedLabelConfirmContent } from './DeleteAssignedLabelConfirmContent';
import { deleteAssignedLabel } from './assignedLabelsApi';
import {
  isAssignedLabelLinkedToRule,
  readTableCell,
} from './assignedLabelsRow';
import { useIsLatestSnapshot } from './useIsLatestSnapshot';

const getDeleteConfirmDialogMessages = (label: string, count = 1) => ({
  header:
    count === 1
      ? 'Assigned label will be deleted'
      : 'Assigned labels will be deleted',
  subHeader:
    count === 1
      ? `Do you really want to delete ${label}?`
      : `Do you really want to delete ${count} selected assigned labels?`,
  btnConfirm: 'Delete',
  btnSecondaryConfirm: 'Cancel',
});

const filterDeletableRows = (rows: PostTablesLabels200DataItem[]) =>
  rows.filter(
    (row) =>
      readTableCell(row.assignment) === 'manual' &&
      readTableCell(row.labelId) !== undefined,
  );

const deleteAssignedLabelRow = (
  snapshotId: string,
  labelId: string,
  reflectToRules: boolean,
) =>
  reflectToRules
    ? deleteAssignedLabel(snapshotId, labelId, true)
    : deleteAssignedLabel(snapshotId, labelId);

type UseAssignedLabelDeleteActionsParams = {
  confirmDeleteDialog: ReturnType<typeof useSimpleConfirmDialog>;
  reloadTable: () => void;
  snapshotId: string;
};

export const useAssignedLabelDeleteActions = ({
  confirmDeleteDialog,
  reloadTable,
  snapshotId,
}: UseAssignedLabelDeleteActionsParams) => {
  const isLatestSnapshot = useIsLatestSnapshot(snapshotId);
  const reflectToRulesRef = useRef(false);

  const deleteAssignedLabels = useStableCallback(
    async (rows: PostTablesLabels200DataItem[]) => {
      for (const row of filterDeletableRows(rows)) {
        const labelId = readTableCell(row.labelId);

        if (!labelId) {
          continue;
        }

        const rowReflectToRules =
          reflectToRulesRef.current && isAssignedLabelLinkedToRule(row);

        await deleteAssignedLabelRow(snapshotId, labelId, rowReflectToRules);
      }

      reloadTable();
    },
  );

  const requestDeleteAssignedLabels = useStableCallback(
    async (rows: PostTablesLabels200DataItem[]): Promise<boolean> => {
      const deletableRows = filterDeletableRows(rows);
      if (deletableRows.length === 0) {
        return false;
      }

      reflectToRulesRef.current =
        isLatestSnapshot &&
        deletableRows.every((row) => isAssignedLabelLinkedToRule(row));
      const canReflectToRules = deletableRows.every((row) =>
        isAssignedLabelLinkedToRule(row),
      );
      const count = deletableRows.length;
      const label =
        count === 1
          ? (readTableCell(deletableRows[0]!.label) ?? 'this label')
          : '';

      if (
        await confirmDeleteDialog.show(
          getDeleteConfirmDialogMessages(label, count),
          {
            children: (
              <DeleteAssignedLabelConfirmContent
                defaultReflectToRules={reflectToRulesRef.current}
                showOlderSnapshotNotice={!isLatestSnapshot}
                showReflectToRules={canReflectToRules}
                onReflectToRulesChange={(value) => {
                  reflectToRulesRef.current = value;
                }}
              />
            ),
          },
        )
      ) {
        await deleteAssignedLabels(deletableRows);
        return true;
      }

      return false;
    },
  );

  const requestDeleteAssignedLabel = useStableCallback(
    async (row: PostTablesLabels200DataItem): Promise<boolean> =>
      requestDeleteAssignedLabels([row]),
  );

  return {
    requestDeleteAssignedLabel,
    requestDeleteAssignedLabels,
  };
};
